import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash2, Shield, Users as UsersIcon, Building2, User2, Eye, ListTree, Settings, UploadCloud, Power, AlertTriangle, Edit3, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDA } from "../lib/i18n";
import ImageUploader from "../components/ImageUploader";

export default function Admin() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const a = t("admin") || {};
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState({ hero_image_url: "", hero_image_alt: "" });

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const reload = () => {
    api.get("/admin/properties", { params: { page: 1, page_size: 100 } }).then((r) => setListings(r.data.items));
    api.get("/admin/users", { params: { page: 1, page_size: 100 } }).then((r) => setUsers(r.data.items));
    api.get("/config").then((r) => setConfig({ hero_image_url: r.data.hero_image_url || "", hero_image_alt: r.data.hero_image_alt || "" }));
  };

  const saveConfig = async () => {
    await api.patch("/admin/config", config);
    toast.success(a.saved);
  };
  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  const setStatus = async (id, status) => {
    await api.patch(`/properties/${id}/status`, null, { params: { status } });
    setListings((arr) => arr.map((p) => p.id === id ? { ...p, status } : p));
  };
  const toggleFeatured = async (id, current) => {
    const next = !current;
    await api.patch(`/admin/properties/${id}/featured`, null, { params: { featured: next } });
    setListings((arr) => arr.map((p) => p.id === id ? { ...p, featured: next } : p));
    toast.success(next ? a.verified : a.unverify);
  };
  const removeListing = async (id) => {
    if (!window.confirm(a.confirmDelete)) return;
    await api.delete(`/properties/${id}`);
    setListings((arr) => arr.filter((p) => p.id !== id));
  };
  const setRole = async (id, role) => {
    await api.patch(`/admin/users/${id}/role`, null, { params: { role } });
    setUsers((arr) => arr.map((u) => u.id === id ? { ...u, role } : u));
    toast.success(a.roleUpdated);
  };
  const removeUser = async (id) => {
    if (!window.confirm(a.confirmDeleteUser)) return;
    await api.delete(`/admin/users/${id}`);
    setUsers((arr) => arr.filter((u) => u.id !== id));
  };
  const toggleActive = async (id, current) => {
    const next = !(current !== false);
    await api.patch(`/admin/users/${id}/active`, null, { params: { active: next } });
    setUsers((arr) => arr.map((u) => u.id === id ? { ...u, is_active: next } : u));
    toast.success(next ? a.accountActivated : a.accountDeactivated);
  };
  const cleanupSeed = async () => {
    if (!window.confirm(a.cleanupConfirm)) return;
    const { data } = await api.delete("/admin/seed/properties");
    toast.success(a.cleanupSuccess(data.deleted));
    reload();
  };

  if (!user || user.role !== "admin") return <div><Header /><div className="pt-32 text-center text-slate-500">…</div></div>;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 max-w-7xl mx-auto px-5 md:px-10 pb-20">
        <div className="flex items-center gap-3 mt-6 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#1B8F5A] flex items-center justify-center"><Shield size={22} className="text-white" /></div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#111827]" data-testid="admin-title">{a.title}</h1>
            <p className="text-slate-500 text-sm">{a.subtitle?.(listings.length, users.length)}</p>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200 mb-6">
          {[["listings", a.tabListings, ListTree], ["users", a.tabUsers, UsersIcon], ["site", a.tabSite, Settings]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 font-semibold text-sm flex items-center gap-2 border-b-2 ${tab === k ? "border-[#1B8F5A] text-[#1B8F5A]" : "border-transparent text-slate-500"}`} data-testid={`admin-tab-${k}`}>
              <I size={15} />{l}
            </button>
          ))}
        </div>

        {tab === "listings" && (
          <>
            <div className="flex justify-end mb-4">
              <Button onClick={cleanupSeed} variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="cleanup-seed">
                <AlertTriangle size={14} className="mr-2" />{a.cleanupSeed}
              </Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {listings.map((p) => (
                <div key={p.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-listing-${p.id}`}>
                  <img src={p.images?.[0] || "https://placehold.co/80x60/e5e7eb/9ca3af?text=N"} className="w-20 h-16 object-cover rounded-lg" alt="" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-400">{p.reference}</span>
                      <span className="text-xs px-2 py-0.5 rounded font-semibold bg-slate-100">{p.status}</span>
                      {p.featured && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">✓ {a.verified}</span>}
                      <span className="text-xs text-slate-500"><Eye size={11} className="inline" /> {p.views || 0}</span>
                    </div>
                    <Link to={`/property/${p.id}`} className="font-semibold text-[#111827] hover:text-[#1B8F5A] line-clamp-1 mt-1">{p.title}</Link>
                    <div className="text-xs text-slate-500 mt-0.5">{p.city}, {p.wilaya} · <span className="font-bold text-[#F59E0B]">{p.price_on_request ? t("priceOnRequest") : formatDA(p.price_da)}</span></div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleFeatured(p.id, p.featured)}
                    title={a.verifyTooltip}
                    className={p.featured ? "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/5" : "text-slate-600"}
                    data-testid={`admin-verify-${p.id}`}
                  >
                    <BadgeCheck size={14} className="mr-1" />{p.featured ? a.unverify : a.verify}
                  </Button>
                  <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                    <SelectTrigger className="w-32 h-9 text-xs" data-testid={`admin-status-${p.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{a.stActive}</SelectItem>
                      <SelectItem value="inactive">{a.stInactive}</SelectItem>
                      <SelectItem value="sold">{a.stSold}</SelectItem>
                      <SelectItem value="rented">{a.stRented}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Link to={`/edit/${p.id}`}><Button size="sm" variant="outline" data-testid={`admin-edit-${p.id}`}><Edit3 size={13} /></Button></Link>
                  <Button size="sm" variant="outline" onClick={() => removeListing(p.id)} className="text-red-600" data-testid={`admin-delete-${p.id}`}><Trash2 size={13} /></Button>
                </div>
              ))}
            </div>
          </div>
          </>
        )}

        {tab === "users" && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {users.map((u) => (
                <div key={u.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-user-${u.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${u.role === "agency" ? "bg-[#F59E0B]/10" : u.role === "admin" ? "bg-[#1B8F5A] text-white" : "bg-slate-100"}`}>
                    {u.role === "agency" ? <Building2 size={17} className="text-[#F59E0B]" /> : <User2 size={17} className={u.role === "admin" ? "text-white" : "text-slate-500"} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${u.id}`} className="font-semibold hover:text-[#1B8F5A]">{u.name}</Link>
                    {u.is_active === false && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-700">{a.deactivated}</span>}
                    <div className="text-xs text-slate-500">{u.email} · {u.phone || "—"}</div>
                  </div>
                  <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                    <SelectTrigger className="w-32 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">{a.rolePrivate}</SelectItem>
                      <SelectItem value="agency">{a.roleAgency}</SelectItem>
                      <SelectItem value="admin">{a.roleAdmin}</SelectItem>
                    </SelectContent>
                  </Select>
                  {u.id !== user.id && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(u.id, u.is_active)} className={u.is_active === false ? "text-emerald-600" : "text-amber-600"} data-testid={`toggle-active-${u.id}`}>
                        <Power size={13} className="mr-1" />{u.is_active === false ? t("activate") : t("deactivate")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => removeUser(u.id)} className="text-red-600"><Trash2 size={13} /></Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "site" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-2xl" data-testid="admin-site-config">
            <h2 className="text-xl font-bold text-[#111827] mb-2">{a.siteHero}</h2>
            <p className="text-sm text-slate-500 mb-6">{a.siteHeroDesc}</p>
            <ImageUploader value={config.hero_image_url ? [config.hero_image_url] : []} onChange={(arr) => setConfig({ ...config, hero_image_url: arr[0] || "" })} max={1} />
            {config.hero_image_url && (
              <Button onClick={() => setConfig({ ...config, hero_image_url: "" })} variant="outline" className="mt-3">{a.resetHero}</Button>
            )}
            <div className="mt-6 pt-6 border-t border-slate-100">
              <Button onClick={saveConfig} className="bg-[#1B8F5A] hover:bg-[#157047]" data-testid="save-config"><UploadCloud size={15} className="mr-2" />{a.save}</Button>
            </div>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}
